import { Box, Text } from "@chakra-ui/react";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { PlaybackButton } from "@/components";
import { KNIT_OFF_MACHINE } from "@/content";
import { usePlaybackToggle } from "@/hooks";

const { steps } = KNIT_OFF_MACHINE;

// The step the video is at, a time into it.
const stepAt = (seconds: number) => {
  let step = 0;
  while (step + 1 < steps.length && steps[step + 1].from <= seconds) step += 1;
  return step;
};

export type OffMachineVideoHandle = {
  readonly showStep: (step: number) => void;
};

type OffMachineVideoProps = {
  readonly onStepChange: (step: number) => void;
};

// The Fabric view's render of Suzanne off the machine (a video): knitting out of the needle bed,
// hanging from the needles, then wrapped onto the model, the step it is at kept in step with a list
// beside it.
export const OffMachineVideo = forwardRef<OffMachineVideoHandle, OffMachineVideoProps>(({ onStepChange }, ref) => {
  const { isPlaying, toggle } = usePlaybackToggle();
  const [step, setStep] = useState(0);
  const [isOnScreen, setIsOnScreen] = useState(false);
  const [isPageVisible, setIsPageVisible] = useState(true);
  const figureRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const onStepChangeRef = useRef(onStepChange);

  useEffect(() => {
    onStepChangeRef.current = onStepChange;
  });

  const showStep = useCallback((index: number) => {
    const video = videoRef.current;
    if (video) video.currentTime = steps[index].from;
  }, []);

  useImperativeHandle(ref, () => ({ showStep }), [showStep]);

  useEffect(() => {
    const figure = figureRef.current;
    if (!figure) return;
    const visible = new IntersectionObserver(([entry]) => setIsOnScreen(entry.isIntersecting), { threshold: 0.25 });
    visible.observe(figure);
    const handleVisibility = () => setIsPageVisible(!document.hidden);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      visible.disconnect();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying && isOnScreen && isPageVisible) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [isPlaying, isOnScreen, isPageVisible]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const follow = () => setStep(stepAt(video.currentTime));
    // timeupdate comes a few times a second while it plays; seeked after a step is picked
    video.addEventListener("timeupdate", follow);
    video.addEventListener("seeked", follow);
    return () => {
      video.removeEventListener("timeupdate", follow);
      video.removeEventListener("seeked", follow);
    };
  }, []);

  useEffect(() => {
    onStepChangeRef.current(step);
  }, [step]);

  return (
    <Box
      ref={figureRef}
      as="figure"
      className="print-avoid-break"
      borderRadius="lg"
      overflow="hidden"
      bg="surface.white"
      border="1px solid"
      borderColor="line.hairline"
    >
      <Box position="relative" sx={{ aspectRatio: { base: "1 / 1", md: "5 / 4" } }}>
        <video
          ref={videoRef}
          src={KNIT_OFF_MACHINE.video.src}
          poster={KNIT_OFF_MACHINE.video.poster}
          width={KNIT_OFF_MACHINE.video.width}
          height={KNIT_OFF_MACHINE.video.height}
          aria-label={`${KNIT_OFF_MACHINE.label}. ${KNIT_OFF_MACHINE.description}`}
          muted
          loop
          playsInline
          preload="none"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }}
        />

        <Text position="absolute" top="18px" left="18px" right="72px" variant="eyebrowBrand" pointerEvents="none">
          {String(step + 1).padStart(2, "0")} · {steps[step].title}
        </Text>

        <PlaybackButton isPlaying={isPlaying} onToggle={toggle} />
      </Box>
    </Box>
  );
});

OffMachineVideo.displayName = "OffMachineVideo";
